/* design.ts — visual style preset module.
 *
 * Mirrors lib/theme.ts in shape. Where theme controls light/dark, design
 * controls the overall visual style — button shape, density, shadow
 * weight, accent intensity. Both are orthogonal: a user can have
 * "dark + editorial" or "light + material" etc.
 *
 * The preset is stored in localStorage and applied as `data-design` on
 * <html>; tokens.css defines the per-design CSS variable overrides so
 * components automatically pick up the active style without per-design
 * branches in their code.
 *
 * Available presets:
 *   refined   — current style, but consistent. Medium radii, subtle
 *               shadows, ghost buttons. The least-disruptive default.
 *   editorial — Claude-like: generous whitespace, sharp corners, very
 *               subtle hairlines instead of borders, restrained accent.
 *               Most "less cartoonish".
 *   material  — Google Material 3: filled buttons, stronger shadows,
 *               saturated accent, raised cards. Corporate productivity.
 *   pill      — embrace the soft pill: rounded ends on every control,
 *               softer cards. Friendliest.
 *
 *   getDesign()       — read current preset (SSR-safe)
 *   setDesign(d)      — persist + apply + broadcast
 *   applyDesign(d)    — DOM side-effect only (data-design attribute)
 *   useDesign()       — React hook returning [design, setDesign]
 */
import React from "react";

// PR 3 — labels updated to match Marco's vocabulary in the feedback:
// "1. Current one  2. Minimalist as Marco Switch  3. Google pieces  4. Claude style"
// IDs kept as-is so existing localStorage values don't break:
//   refined   → "Current"     (what the platform looked like before the system)
//   pill      → "Minimalist"  (Marco Switch slider aesthetic — soft pills)
//   material  → "Google"      (Material 3 — filled, raised, bold)
//   editorial → "Claude"      (claude.ai aesthetic — sharp, restrained)
export type Design = "refined" | "editorial" | "material" | "pill";

export const DESIGNS: Design[] = ["refined", "pill", "material", "editorial"];

export const DESIGN_LABELS: Record<Design, { en: string; it: string }> = {
  refined:   { en: "Current",     it: "Attuale" },
  pill:      { en: "Minimalist",  it: "Minimalista" },
  material:  { en: "Google",      it: "Google" },
  editorial: { en: "Claude",      it: "Claude" },
};

const STORAGE_KEY = "forecast_design";
const EVENT_NAME = "forecast-design-change";
const DEFAULT_DESIGN: Design = "refined";

function isDesign(v: unknown): v is Design {
  return v === "refined" || v === "editorial" || v === "material" || v === "pill";
}

/** First-load resolution — read persisted value, else "refined". SSR-safe. */
export function getDesign(): Design {
  if (typeof window === "undefined") return DEFAULT_DESIGN;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isDesign(stored)) return stored;
  } catch {}
  return DEFAULT_DESIGN;
}

/** Apply design to the document (data-design attribute on <html>). */
export function applyDesign(design: Design): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-design", design);
}

/** Set design: persist + apply + broadcast. */
export function setDesign(design: Design): void {
  if (typeof window === "undefined") return;
  if (!isDesign(design)) return;
  applyDesign(design);
  try { localStorage.setItem(STORAGE_KEY, design); } catch {}
  try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: design })); } catch {}
}

/** React hook — returns [design, setDesign]. Re-renders on cross-tab
 *  storage events AND same-tab custom events. */
export function useDesign(): [Design, (design: Design) => void] {
  const [design, setLocal] = React.useState<Design>(() => getDesign());

  React.useEffect(() => {
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (isDesign(detail)) setLocal(detail);
      else setLocal(getDesign());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLocal(getDesign());
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // First mount: ensure the DOM matches the resolved initial design.
  React.useEffect(() => {
    applyDesign(design);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [design, setDesign];
}
